import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Building2,
  ChevronRight,
  Copy,
  Eye,
  FileText,
  Pencil,
  Plus,
  Search,
  Trash2,
  Upload,
} from "lucide-react";
import SiteForm from "@/components/SiteForm";
import {
  deleteSite,
  deleteSiteRuleSection,
  listBaysLite,
  listSites,
  listSiteRuleSections,
  siteBayCount,
  upsertSiteRuleSection,
  uploadSiteRuleFile,
} from "@/lib/api";
import { useViewAsClient } from "@/lib/viewAsClient";

export default function Sites() {
  const { viewAsClientId } = useViewAsClient();
  const [sites, setSites] = useState([]);
  const [bayCounts, setBayCounts] = useState({});
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null); // site row OR { __new: true }
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set()); // siteIds expanded inline

  const toggleExpanded = (siteId) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(siteId) ? n.delete(siteId) : n.add(siteId);
      return n;
    });
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const { data, error } = await listSites({ search, activeOnly, clientId: viewAsClientId || null });
    if (error) {
      toast.error(error.message);
      setLoading(false);
      return;
    }
    setSites(data || []);
    setLoading(false);

    // fetch bay counts in parallel (non-blocking)
    const counts = {};
    await Promise.all(
      (data || []).map(async (s) => {
        counts[s.id] = await siteBayCount(s.id);
      })
    );
    setBayCounts(counts);
  }, [search, activeOnly, viewAsClientId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await listSites({ search, activeOnly, clientId: viewAsClientId || null });
      if (cancelled) return;
      if (error) {
        toast.error(error.message);
        setLoading(false);
        return;
      }
      setSites(data || []);
      setLoading(false);
      const counts = {};
      await Promise.all(
        (data || []).map(async (s) => {
          counts[s.id] = await siteBayCount(s.id);
        })
      );
      if (!cancelled) setBayCounts(counts);
    })();
    return () => {
      cancelled = true;
    };
  }, [search, activeOnly, viewAsClientId]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const { error } = await deleteSite(confirmDelete.id);
    setConfirmDelete(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Site deleted");
    fetchAll();
  };

  // Open the form pre-filled from an existing site. The new row keeps all
  // settings (timezone, hours, contact, policy overrides, etc.) but drops
  // the id / timestamps and suffixes name/code so the unique constraints
  // are not violated. Saving from the form does a normal INSERT.
  const startDuplicate = (s) => {
    const { id, created_at, updated_at, ...rest } = s;
    setEditing({
      ...rest,
      name: `${s.name} (copy)`,
      code: s.code ? `${s.code}-COPY` : "",
    });
  };

  const showCounts = useMemo(() => Object.keys(bayCounts).length > 0, [bayCounts]);

  return (
    <div data-testid="sites-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Sites &amp; bays</h1>
          <p className="text-sm text-slate-500">
            Locations under your tenant. Click a site to manage its loading bays.
          </p>
        </div>
        <Button onClick={() => setEditing({ __new: true })} data-testid="add-site-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add site
        </Button>
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="py-3 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="sites-search-input"
              className="pl-9"
              placeholder="Search name, code or city"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="active-only"
              checked={activeOnly}
              onCheckedChange={setActiveOnly}
              data-testid="sites-active-only-switch"
            />
            <Label htmlFor="active-only" className="text-sm">
              Active only
            </Label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">
            {loading ? "Loading…" : `${sites.length} site${sites.length === 1 ? "" : "s"}`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {sites.length === 0 && !loading ? (
            <EmptyState onAdd={() => setEditing({ __new: true })} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Site</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>City / state</TableHead>
                    <TableHead>Hours</TableHead>
                    <TableHead className="text-right">Bays</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-12 text-right">&nbsp;</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sites.map((s) => {
                    const isOpen = expanded.has(s.id);
                    return (
                    <Fragment key={s.id}>
                    <TableRow
                      data-testid={`site-row-${s.id}`}
                      className="hover:bg-slate-50"
                    >
                      <TableCell className="py-2">
                        <button
                          onClick={() => toggleExpanded(s.id)}
                          className="h-5 w-5 inline-flex items-center justify-center rounded hover:bg-slate-100"
                          data-testid={`site-expand-${s.id}`}
                          aria-label={isOpen ? "Collapse bays" : "Expand bays"}
                        >
                          <ChevronRight className={`h-4 w-4 text-slate-500 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                        </button>
                      </TableCell>
                      <TableCell>
                        <Link
                          to={`/sites/${s.id}`}
                          className="font-medium text-slate-900 hover:underline"
                          data-testid={`site-name-link-${s.id}`}
                        >
                          {s.name}
                        </Link>
                        <div className="text-xs text-slate-500">{s.timezone}</div>
                      </TableCell>
                      <TableCell className="text-sm tabular-nums">{s.code || "—"}</TableCell>
                      <TableCell className="text-sm">
                        {[s.city, s.state].filter(Boolean).join(", ") || "—"}
                      </TableCell>
                      <TableCell className="text-sm tabular-nums">
                        {s.operating_hours_start?.slice(0, 5)} – {s.operating_hours_end?.slice(0, 5)}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {showCounts ? bayCounts[s.id] ?? 0 : "…"}
                      </TableCell>
                      <TableCell>
                        {s.is_active ? (
                          <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-0.5">
                          <Button asChild variant="ghost" size="icon" className="h-8 w-8" title="View bays">
                            <Link to={`/sites/${s.id}`} data-testid={`site-view-${s.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit site"
                                  onClick={() => setEditing(s)} data-testid={`site-edit-${s.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Duplicate site"
                                  onClick={() => startDuplicate(s)} data-testid={`site-duplicate-${s.id}`}>
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" title="Delete site"
                                  onClick={() => setConfirmDelete(s)} data-testid={`site-delete-${s.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow className="bg-slate-50/40">
                        <TableCell />
                        <TableCell colSpan={7} className="py-3">
                          <SiteInlineDetail site={s} />
                        </TableCell>
                      </TableRow>
                    )}
                    </Fragment>
                  );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <SiteForm
        open={!!editing}
        onOpenChange={(open) => !open && setEditing(null)}
        site={editing?.__new ? null : editing}
        onSaved={fetchAll}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete site?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{confirmDelete?.name}</strong> and all of its bays.
              Bookings tied to those bays will also be removed via cascade. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-site"
            >
              Yes, delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="py-12 text-center" data-testid="sites-empty-state">
      <div className="mx-auto h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
        <Building2 className="h-6 w-6 text-slate-400" />
      </div>
      <h3 className="text-sm font-medium">No sites yet</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
        Create your first site to start configuring bays and accepting bookings.
      </p>
      <Button className="mt-4" onClick={onAdd} data-testid="empty-add-site-btn">
        <Plus className="h-4 w-4 mr-1.5" /> Add site
      </Button>
    </div>
  );
}

/**
 * Inline expansion under each site row.
 *  - Lists all bays at the site (indented, plain rows — no nested table
 *    sidebars to keep visual hierarchy clean).
 *  - Holds the Site Map PDF upload (was in Client Settings — moved here
 *    because the map is site-scoped). Map is stored as a `file_upload`
 *    section so it appears on the public driver site-info page.
 */
function SiteInlineDetail({ site }) {
  const [bays, setBays] = useState(null);
  const [siteMap, setSiteMap] = useState(null); // section row or null
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: b }, { data: secs }] = await Promise.all([
        listBaysLite(site.id, { includeInactive: true }),
        listSiteRuleSections(site.id),
      ]);
      if (cancelled) return;
      setBays(b || []);
      setSiteMap((secs || []).find((sec) => sec.title === "Site map PDF") || null);
    })();
    return () => { cancelled = true; };
  }, [site.id]);

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const { data: up, error } = await uploadSiteRuleFile(site.id, file);
    if (error) { setUploading(false); toast.error(error.message); return; }
    const payload = {
      id: siteMap?.id,
      site_id: site.id,
      section_type: "file_upload",
      title: "Site map PDF",
      display_order: 999,
      body_markdown: "",
      data: { files: [{ name: file.name, url: up.url, path: up.path }] },
      is_internal: false, // visible on driver site-info page by default
    };
    const { data: saved, error: upErr } = await upsertSiteRuleSection(payload);
    setUploading(false);
    if (upErr) { toast.error(upErr.message); return; }
    setSiteMap(Array.isArray(saved) ? saved[0] : saved);
    toast.success("Site map uploaded.");
  };

  const onRemove = async () => {
    if (!siteMap) return;
    if (!window.confirm("Remove the current site map PDF?")) return;
    const { error } = await deleteSiteRuleSection(siteMap.id);
    if (error) { toast.error(error.message); return; }
    setSiteMap(null);
    toast.success("Site map removed.");
  };

  return (
    <div className="pl-6 pr-2 space-y-3" data-testid={`site-inline-${site.id}`}>
      {/* Bays — indented list */}
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">
          Bays {bays ? `(${bays.length})` : ""}
        </div>
        {!bays ? (
          <div className="text-xs text-slate-400">Loading…</div>
        ) : bays.length === 0 ? (
          <div className="text-xs text-slate-400 italic">
            No bays yet. <Link to={`/sites/${site.id}`} className="text-blue-600 hover:underline">Add one →</Link>
          </div>
        ) : (
          <ul className="space-y-0.5">
            {bays.map((b) => (
              <li key={b.id} className="flex items-center gap-2 text-sm text-slate-700">
                <span className="h-2 w-2 rounded-full" style={{ background: b.color || "#94a3b8" }} />
                <span className="font-medium">{b.name}</span>
                {b.code && <span className="text-xs text-slate-400">({b.code})</span>}
                {b.slot_duration_minutes && (
                  <span className="text-xs text-slate-400 ml-auto">{b.slot_duration_minutes} min slot</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Site map */}
      <div className="pt-2 border-t border-slate-200">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
          <FileText className="h-3 w-3" /> Site map
        </div>
        {siteMap?.data?.files?.[0] ? (
          <div className="flex items-center gap-3 text-sm">
            <a
              href={siteMap.data.files[0].url}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:underline"
              data-testid={`site-map-link-${site.id}`}
            >
              {siteMap.data.files[0].name || "View site map"}
            </a>
            <label className="text-xs text-blue-600 hover:underline cursor-pointer">
              Replace
              <input
                type="file"
                accept="application/pdf,image/*"
                className="hidden"
                onChange={(e) => onUpload(e.target.files?.[0])}
                disabled={uploading}
                data-testid={`site-map-replace-${site.id}`}
              />
            </label>
            <button
              onClick={onRemove}
              className="text-xs text-rose-600 hover:underline"
              data-testid={`site-map-remove-${site.id}`}
            >
              Remove
            </button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded border border-slate-300 hover:bg-slate-50 cursor-pointer">
            <Upload className="h-3 w-3" />
            {uploading ? "Uploading…" : "Upload site map PDF"}
            <input
              type="file"
              accept="application/pdf,image/*"
              className="hidden"
              onChange={(e) => onUpload(e.target.files?.[0])}
              disabled={uploading}
              data-testid={`site-map-upload-${site.id}`}
            />
          </label>
        )}
      </div>
    </div>
  );
}
